import type { IGtoProcedureCustomControlData } from '../../../../../../common/validation/customControls/general';
import type { ICustomControlProcedureReadProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcedureStatus } from '../../../../../../common/enums/procedureStatus';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { ProcedureReadButtons } from '../../../../elements/carePathButtons/ProcedureReadButtons';
import { FormParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { GtoV1ProcedureSummary } from './GtoV1ProcedureSummary';

export function GtoV1ProcedureRead(props: ICustomControlProcedureReadProps) {
    const { patient } = useEpisodeContext();
    const customControlData: IGtoProcedureCustomControlData = props.procedure.customControlData;

    if (props.procedure.status !== ProcedureStatus.completed) {
        return null;
    }

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.process.procedure && props.process.procedure.dateSent}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                organizationName={
                    patient.authorization?.canSeePatientDetails && props.process.procedure?.performerOrganization?.name
                        ? props.process.procedure.performerOrganization.name
                        : undefined
                }
                practitionerName={
                    patient.authorization?.canSeePatientDetails && props.process.procedure?.performerPractitioner
                        ? getDisplayName(props.process.procedure.performerPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                showProcessIcon
                title={getServiceLabel(props.process.code, 'procedure', props.process.name)}
            />

            {customControlData && (
                <>
                    <GtoV1ProcedureSummary {...props} />

                    <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                        <HorizontalFormGroupRead>
                            <FormLabel>Hoeveel last heb je van de klachten?</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{customControlData.complaintsSeverity}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>

                        <HorizontalFormGroupRead>
                            <FormLabel>Waar voel je de klachten precies?</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{customControlData.complaintsLocation}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>

                        <HorizontalFormGroupRead>
                            <FormLabel>Hoelang heb je last van de klachten?</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{customControlData.complaintsDuration}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>

                        <HorizontalFormGroupRead>
                            <FormLabel>Zijn er bijkomende klachten of symptomen?</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>
                                    {customControlData.areThereAdditionalComplaintsOrSymptons}
                                </FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>

                        {customControlData.areThereAdditionalComplaintsOrSymptons === 'Ja' && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Welke zijn dit?</FormLabel>
                                <InputContainer>
                                    <FormParagraph bold>
                                        {customControlData.areThereAdditionalComplaintsOrSymptonsRemark}
                                    </FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        <HorizontalFormGroupRead>
                            <FormLabel>Heb je koorts?</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{customControlData.doYouHaveFever}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>

                        {customControlData.doYouHaveFever === 'Ja' && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Hoe heb je dit gemeten?</FormLabel>
                                <InputContainer>
                                    <FormParagraph bold>{customControlData.doYouHaveFeverRemark}</FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        <HorizontalFormGroupRead>
                            <FormLabel>Ben je wel eens in het ziekenhuis opgenomen?</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{customControlData.haveYouEverBeenHospitalized}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>

                        {customControlData.haveYouEverBeenHospitalized === 'Ja' && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Waarvoor?</FormLabel>
                                <InputContainer>
                                    <FormParagraph bold>
                                        {customControlData.haveYouEverBeenHospitalizedRemark}
                                    </FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {customControlData.areYouBeingTreated && (
                            <>
                                <HorizontalFormGroupRead>
                                    <FormLabel>Ben je onder behandeling door specialist of huisarts?</FormLabel>
                                    <InputContainer>
                                        <FormParagraph bold>{customControlData.areYouBeingTreated}</FormParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>

                                {customControlData.areYouBeingTreated === 'Ja' && (
                                    <HorizontalFormGroupRead>
                                        <FormLabel>Waarvoor?</FormLabel>
                                        <InputContainer>
                                            <FormParagraph bold>
                                                {customControlData.areYouBeingTreatedRemark}
                                            </FormParagraph>
                                        </InputContainer>
                                    </HorizontalFormGroupRead>
                                )}
                            </>
                        )}

                        {customControlData.areYouOnMedication && (
                            <>
                                <HorizontalFormGroupRead>
                                    <FormLabel>Gebruik je medicijnen, pijnstillers of de pil?</FormLabel>
                                    <InputContainer>
                                        <FormParagraph bold>{customControlData.areYouOnMedication}</FormParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>

                                {customControlData.areYouOnMedication === 'Ja' && (
                                    <HorizontalFormGroupRead>
                                        <FormLabel>Welke en hoeveel?</FormLabel>
                                        <InputContainer>
                                            <FormParagraph bold>
                                                {customControlData.areYouOnMedicationRemark}
                                            </FormParagraph>
                                        </InputContainer>
                                    </HorizontalFormGroupRead>
                                )}
                            </>
                        )}

                        <ProcedureReadButtons {...props} />
                    </CustomControlCollapseBody>

                    <ProcessItemFooter
                        isItemOpen={props.itemState.isOpen}
                        labelLess={translateKey('procedure.hideProcedure')}
                        labelMore={translateKey('procedure.showProcedure')}
                        onToggle={async (isOpened) =>
                            await props.updateItemState({ ...props.itemState, isOpen: isOpened })
                        }
                    />
                </>
            )}
        </ProcessContainer>
    );
}
